import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, Grid, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  TablePagination, Divider, Chip, MenuItem, CircularProgress, Autocomplete
} from "@mui/material";
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon,
  Delete as DeleteIcon, Hotel as BedIcon
} from "@mui/icons-material";
import { db, auth } from "../../firebase";
import {
  collection, addDoc, getDocs, getDoc, query, orderBy, doc,
  updateDoc, deleteDoc, Timestamp, where
} from "firebase/firestore";
import Swal from "sweetalert2";

const PRIMARY = "#0d9488";

function Roomallocation() {
  const [allocations, setAllocations] = useState([]);
  const [students, setStudents] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bedOptions, setBedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  const [search, setSearch] = useState("");
  const [filterHostel, setFilterHostel] = useState("All"); // New state for hostel filter
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState({
    studentId: "",
    studentName: "",
    hostelId: "",
    roomId: "",
    bedno: "",
    joiningDate: new Date().toISOString().split('T')[0],
    status: "Live"
  });

  useEffect(() => {
    const init = async () => {
      if (!auth.currentUser) return;
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (userSnap.exists()) {
        const sId = userSnap.data().schoolId;
        setSchoolId(sId);
        fetchData(sId);
      }
    };
    init();
  }, []);

  const fetchData = async (sId) => {
    setLoading(true);
    try {
      const [admSnap, studentSnap, courseSnap, hostelSnap, allocationSnap] = await Promise.all([
        getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where("studenttype", "==", "Boarding"), where('status', '==', 'Active'))),
        getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
        getDocs(query(collection(db, "courses"), where("schoolId", "==", sId))),
        getDocs(query(collection(db, "hostels"), where("schoolId", "==", sId))),
        getDocs(query(collection(db, "roomallocations"), where("schoolId", "==", sId)))
      ]);

      const studentMap = {};
      studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });

      const courseMap = {};
      courseSnap.docs.forEach(d => { courseMap[d.id] = d.data(); });

      const studentList = admSnap.docs.map((adm) => {
        const admData = adm.data();
        const sData = studentMap[admData.studentId];
        const className = courseMap[admData.class]?.name || "N/A";
        return {
          id: admData.studentId,
          label: `${sData?.firstname || ''} ${sData?.fathername || ''} ${sData?.surname || ''} - (${className})`.toUpperCase(),
        };
      }).sort((a, b) => a.label.localeCompare(b.label));

      // SORT ALLOCATIONS ASCENDING BY STUDENT NAME
      const allocationList = allocationSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.studentName.localeCompare(b.studentName));

      setStudents(studentList);
      setHostels(hostelSnap.docs.map(d => ({ id: d.id, name: d.data().hostelName })));
      setAllocations(allocationList);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async (hId) => {
    const rSnap = await getDocs(query(collection(db, "hostelrooms"), where("hostelId", "==", hId), where("status", "==", "Available")));
    const roomList = rSnap.docs.map(d => ({ id: d.id, roomNo: d.data().roomNo, beds: parseInt(d.data().noOfBeds || 0) }));
    setRooms(roomList);
    return roomList;
  };

  const handleRoomChange = (roomNo, currentRooms = rooms) => {
    const selectedRoom = currentRooms.find(r => r.roomNo === roomNo);
    if (selectedRoom) {
      const beds = Array.from({ length: selectedRoom.beds }, (_, i) => i + 1);
      setBedOptions(beds);
      setForm(prev => ({ ...prev, roomId: roomNo, bedno: "" }));
    }
  };

  const handleEdit = async (row) => {
    setLoading(true);
    setEditId(row.id);
    const availableRooms = await fetchRooms(row.hostelId);
    const selectedRoom = availableRooms.find(r => r.roomNo === row.roomId);
    if (selectedRoom) {
      setBedOptions(Array.from({ length: selectedRoom.beds }, (_, i) => i + 1));
    }
    setForm({ ...row });
    setOpen(true);
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.studentId || !form.roomId || !form.bedno) {
      return Swal.fire("Error", "All fields are required", "error");
    }

    setLoading(true);
    try {
      const payload = { ...form, schoolId, updatedAt: Timestamp.now() };
      if (editId) {
        await updateDoc(doc(db, "roomallocations", editId), payload);
        Swal.fire("Updated", "Allocation updated", "success");
      } else {
        await addDoc(collection(db, "roomallocations"), payload);
        Swal.fire("Success", "Room Allocated", "success");
      }
      setOpen(false);
      resetForm();
      fetchData(schoolId);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditId(null);
    setForm({
      studentId: "", studentName: "", hostelId: "", roomId: "",
      bedno: "", joiningDate: new Date().toISOString().split('T')[0], status: "Live"
    });
    setBedOptions([]);
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({
      title: "Confirm Delete?",
      text: "Remove this student from the room?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33"
    });
    if (res.isConfirmed) {
      await deleteDoc(doc(db, "roomallocations", id));
      fetchData(schoolId);
      Swal.fire("Deleted", "Allocation removed", "success");
    }
  };

  // Filter logic for searching and hostel selection
  const filteredData = allocations.filter(a => {
    const matchSearch = a.studentName.toLowerCase().includes(search.toLowerCase());
    const matchHostel = filterHostel === "All" ? true : a.hostelId === filterHostel;
    return matchSearch && matchHostel;
  });

  return (
    <Box p={1} sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header Section */}
      <Stack direction="row" justifyContent="space-between" mb={3} alignItems="center">
        <Box>
          <Typography variant="h6" fontWeight="bold">Hostel Room Allocation</Typography>
          <Typography variant="caption" color="textSecondary">Assign and manage boarding students</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: PRIMARY }} onClick={() => { resetForm(); setOpen(true); }}>
          New Allocation
        </Button>
      </Stack>

      {/* Table Section */}
      <Paper sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }} elevation={0}>
        
        {/* Filter & Search Bar */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} p={2} bgcolor="#fff" alignItems="center">
          <TextField
            fullWidth size="small" placeholder="Search by student name..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} /> }}
          />
          <TextField
            select
            size="small"
            value={filterHostel}
            onChange={(e) => { setFilterHostel(e.target.value); setPage(0); }}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          >
            <MenuItem value="All">All Hostels</MenuItem>
            {hostels.map(h => (
              <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>
            ))}
          </TextField>
          <Chip 
            label={`Total: ${filteredData.length}`} 
            color="primary" 
            variant="outlined" 
            sx={{ fontWeight: 'bold', minWidth: '100px', fontSize: '0.9rem' }} 
          />
        </Stack>
        
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell align="center" width="60"><b>Sr.</b></TableCell>
                <TableCell><b>Student Name</b></TableCell>
                <TableCell><b>Hostel & Room</b></TableCell>
                <TableCell align="center"><b>Bed No</b></TableCell>
                <TableCell><b>Joining Date</b></TableCell>
                <TableCell align="center"><b>Status</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && filteredData.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={20} sx={{ my: 2 }} /></TableCell></TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><Typography p={2} color="textSecondary">No allocations found</Typography></TableCell></TableRow>
              ) : (
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.id} hover>
                      <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">{row.studentName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">
                            {hostels.find(h => h.id === row.hostelId)?.name || "N/A"}
                          </Typography>
                          <Chip label={`Room: ${row.roomId}`} size="small" variant="outlined" color="primary" />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={row.bedno} size="small" sx={{ fontWeight: 'bold', bgcolor: '#e0f2f1', color: PRIMARY }} />
                      </TableCell>
                      <TableCell>{row.joiningDate}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={row.status} 
                          size="small" 
                          sx={{ 
                            color: row.status === "Live" ? "#15803d" : "#94a3b8",
                            borderColor: row.status === "Live" ? "#bbf7d0" : "#e2e8f0",
                            bgcolor: row.status === "Live" ? "#f0fdf4" : "#f8fafc"
                          }} 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" justifyContent="center">
                          <IconButton size="small" sx={{ color: PRIMARY }} onClick={() => handleEdit(row)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(e, n) => setPage(n)} />
      </Paper>

      {/* ALLOCATION DIALOG */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          <BedIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="primary" />
          {editId ? "Edit Allocation" : "New Room Allocation"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Grid className="row" spacing={2} mt={1}>
            <Grid className="col-sm-12 mb-3" item xs={12}>
              <Autocomplete
                options={students}
                value={students.find(s => s.id === form.studentId) || null}
                getOptionLabel={(option) => option.label}
                onChange={(e, val) => setForm({ ...form, studentId: val?.id || "", studentName: val?.label.split('-')[0].trim() || "" })}
                renderInput={(params) => <TextField {...params} label="Search Boarding Student" size="small" fullWidth />}
              />
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField select label="Select Hostel" fullWidth size="small" value={form.hostelId} onChange={(e) => { setForm({ ...form, hostelId: e.target.value }); fetchRooms(e.target.value); }}>
                {hostels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={6}>
              <TextField select label="Room" fullWidth size="small" value={form.roomId} onChange={(e) => handleRoomChange(e.target.value)} disabled={!form.hostelId}>
                {rooms.map(r => <MenuItem key={r.id} value={r.roomNo}>Room {r.roomNo} ({r.beds} Beds)</MenuItem>)}
              </TextField>
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={6}>
              <TextField label="Bed No" fullWidth size="small" value={form.bedno} onChange={(e) => setForm({ ...form, bedno: e.target.value })} />
            </Grid>
            <Grid className="col-sm-6 mb-3" item xs={12}>
              <TextField type="date" label="Joining Date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.joiningDate} onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} />
            </Grid>
          </Grid>
          <Button variant="contained" fullWidth sx={{ mt: 3, py: 1.2, bgcolor: PRIMARY, fontWeight: 'bold' }} onClick={handleSubmit} disabled={loading}>
            {editId ? "Update Allocation" : "Confirm Allocation"}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Roomallocation;